import { Button, Chip, Input, Spinner } from '@sjuni/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { FormError } from '@/components/FormError';
import { DocumentPicker } from '@/components/documents/DocumentPicker';
import { PublishingSidebar } from '@/components/editor/PublishingSidebar';
import { RichTextField } from '@/components/editor/RichTextField';
import { TranslatableField } from '@/components/form/TranslatableField';
import { TranslationProgress } from '@/components/form/TranslationProgress';
import { ApiRequestError, apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import type { TranslatedValue } from '@/lib/forms/translatable';
import { valueFor } from '@/lib/forms/translatable';

import type { ModalContactBlock, ModalRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.3 — `/modals` · `/:key`.
 *
 * "A simple list of the nine modal keys with title, translation status, and
 * last-updated" and an editor with "a preview that renders the modal exactly as
 * the public site does — these are long documents and formatting mistakes are
 * easy to miss in a plain editor."
 */
export function ModalsPage() {
  const rest = (useParams()['*'] ?? '').replace(/\/+$/, '');

  return rest === '' ? <List /> : <Editor modalKey={rest} />;
}

const KEY = ['admin', 'modals'];

function List() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locale } = useEditorLocale();

  const modals = useQuery({
    queryKey: KEY,
    queryFn: () => apiFetch<{ data: ModalRow[] }>('/admin/modals'),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-h2 font-extrabold text-primary">{t('nav.items.modals')}</h1>
        <p className="mt-1 text-body-sm text-secondary">{t('modals.intro')}</p>
      </div>

      {modals.isError ? <FormError message={t('errors.generic')} /> : null}
      {modals.isPending ? <Spinner size="lg" label={t('common.loading')} /> : null}

      <div className="overflow-x-auto rounded-card border border-subtle">
        <table className="w-full text-body-sm">
          <caption className="sr-only">{t('nav.items.modals')}</caption>

          <thead className="bg-surface-subtle border-b border-subtle">
            <tr>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('modals.columns.title')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('modals.columns.translations')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('modals.columns.updated')}
              </th>
              <th scope="col" className="p-3 text-start font-extrabold text-primary">
                {t('content.columns.status')}
              </th>
            </tr>
          </thead>

          <tbody>
            {(modals.data?.data ?? []).map((modal) => (
              <tr
                key={modal.key}
                tabIndex={0}
                className="hover:bg-surface-subtle cursor-pointer border-b border-subtle last:border-0 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-navy-600"
                onClick={() => navigate(`/modals/${modal.key}`)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    navigate(`/modals/${modal.key}`);
                  }
                }}
              >
                <td className="p-3">
                  <span className="font-semibold text-primary">
                    {valueFor(modal.title, locale) || modal.key}
                  </span>
                  {/* The key is what `/students?modal=…` links to, so it is
                      worth showing beside the title rather than hiding. */}
                  <span className="mt-0.5 block text-caption text-muted">{modal.key}</span>
                </td>

                <td className="p-3">
                  <span className="flex flex-wrap gap-1">
                    {['ka', 'en'].map((code) => (
                      <Chip
                        key={code}
                        tone={modal.translations_available.includes(code) ? 'navy' : 'neutral'}
                      >
                        {code.toUpperCase()}
                      </Chip>
                    ))}
                  </span>
                </td>

                <td className="p-3 text-secondary">
                  {modal.updated_at ? new Date(modal.updated_at).toLocaleDateString(locale) : '—'}
                </td>

                <td className="p-3">
                  <Chip tone={modal.status === 'published' ? 'navy' : 'gold'}>
                    {t(`editor.publishing.statuses.${modal.status}`)}
                  </Chip>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Editor({ modalKey }: { modalKey: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [error, setError] = useState<string | null>(null);

  const modal = useQuery({
    queryKey: [...KEY, modalKey],
    queryFn: () => apiFetch<{ data: ModalRow }>(`/admin/modals/${modalKey}`),
  });

  const record = modal.data?.data;

  if (modal.isPending) {
    return (
      <div className="grid min-h-96 place-items-center">
        <Spinner size="lg" label={t('common.loading')} />
      </div>
    );
  }

  if (!record) {
    return <FormError message={t('errors.generic')} />;
  }

  return (
    <Form
      key={record.key}
      record={record}
      error={error}
      onError={setError}
      onSaved={() => {
        void queryClient.invalidateQueries({ queryKey: KEY });
        navigate('/modals');
      }}
    />
  );
}

interface Fields {
  title: TranslatedValue;
  lead: TranslatedValue;
  body: TranslatedValue;
  cta_label: TranslatedValue;
  cta_url: string;
  /*
   * The document itself rather than its id, so the form can show an editor what
   * is attached. The id is all the API needs; the title is what makes the field
   * legible, and it is already on the record.
   */
  cta_document: { id: number; title: TranslatedValue } | null;
  contact: ModalContactBlock;
  status: ModalRow['status'];
  published_at: string | null;
}

function Form({
  record,
  error,
  onError,
  onSaved,
}: {
  record: ModalRow;
  error: string | null;
  onError: (message: string | null) => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [fields, setFields] = useState<Fields>({
    title: record.title,
    lead: record.lead,
    body: record.body,
    cta_label: record.cta_label,
    cta_url: record.cta_url ?? '',
    cta_document: record.cta_document,
    contact: record.contact_block ?? {},
    status: record.status,
    published_at: record.published_at,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = <K extends keyof Fields>(key: K, value: Fields[K]): void =>
    setFields((current) => ({ ...current, [key]: value }));

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/admin/modals/${record.key}`, {
        method: 'PUT',
        body: {
          title: fields.title,
          lead: fields.lead,
          body: fields.body,
          cta_label: fields.cta_label,
          cta_url: fields.cta_url.trim() === '' ? null : fields.cta_url.trim(),
          cta_document_id: fields.cta_document?.id ?? null,
          // An empty object rather than `{}` worth of nulls: §4 makes the
          // whole block optional, and a modal with no contact footer should
          // store nothing rather than a shape full of blanks.
          contact_block: hasContact(fields.contact) ? fields.contact : null,
          status: fields.status,
          published_at: fields.published_at,
        },
      }),
    onSuccess: onSaved,
    onError: (cause: unknown) => {
      if (cause instanceof ApiRequestError) {
        // The first message per field. §6 returns a list, and a field can only
        // show one — the rest are almost always consequences of the first.
        setErrors(
          Object.fromEntries(
            Object.entries(cause.validationErrors).map(([field, messages]) => [
              field,
              messages[0] ?? '',
            ]),
          ),
        );
      }

      onError(cause instanceof ApiRequestError ? cause.message : t('errors.generic'));
    },
  });

  return (
    <form
      className="mx-auto max-w-5xl"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        onError(null);
        setErrors({});
        save.mutate();
      }}
    >
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-h2 font-extrabold text-primary">{t('modals.edit')}</h1>
          <p className="mt-1 text-caption text-muted">{record.key}</p>
        </div>

        <Button type="button" variant="ghost" size="sm" onClick={() => navigate('/modals')}>
          {t('common.back')}
        </Button>
      </div>

      <FormError message={error} />

      <div className="grid gap-8 lg:grid-cols-[1fr_280px]">
        <div className="space-y-5">
          <TranslatableField
            label={t('modals.fields.title')}
            name="title"
            value={fields.title}
            onChange={(next) => set('title', next)}
            error={errors['title.ka']}
          />

          {/* §4 — "the bold intro paragraph several modals open with". Plain
              text: it is rendered at `body-lg` semibold, not as markup. */}
          <TranslatableField
            label={t('modals.fields.lead')}
            hint={t('modals.fields.leadHint')}
            name="lead"
            multiline
            rows={2}
            value={fields.lead}
            onChange={(next) => set('lead', next)}
            required={false}
          />

          <RichTextField
            label={t('content.fields.body')}
            value={fields.body}
            onChange={(next) => set('body', next)}
          />

          <fieldset className="grid gap-4 rounded-card border border-subtle p-4 sm:grid-cols-2">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('modals.cta')}
            </legend>

            <div className="sm:col-span-2">
              <TranslatableField
                label={t('modals.fields.ctaLabel')}
                name="cta_label"
                value={fields.cta_label}
                onChange={(next) => set('cta_label', next)}
                required={false}
              />
            </div>

            {/*
              §4 gives a modal both a document and a URL, and the server
              refuses both at once — so the hint says which to pick rather
              than letting an editor find out by being refused.

              This was a number box an editor typed a database id into: they had
              to look one up on the documents screen, and a wrong digit produced
              a button that downloaded the wrong regulation with nothing on the
              form to show it.
            */}
            <DocumentPicker
              label={t('modals.fields.ctaDocument')}
              hint={t('modals.fields.ctaDocumentHint')}
              value={fields.cta_document}
              onChange={(next) => set('cta_document', next)}
              error={errors.cta_document_id}
            />

            <Input
              label={t('modals.fields.ctaUrl')}
              value={fields.cta_url}
              onChange={(event) => set('cta_url', event.target.value)}
              error={errors.cta_url}
            />
          </fieldset>

          {/* §4's optional structured footer — the Preparation Centre modal. */}
          <fieldset className="grid gap-4 rounded-card border border-subtle p-4 sm:grid-cols-2">
            <legend className="px-1 text-caption font-extrabold tracking-wide text-muted">
              {t('modals.contact')}
            </legend>

            <div className="sm:col-span-2">
              <TranslatableField
                label={t('modals.fields.address')}
                name="address"
                value={fields.contact.address ?? {}}
                onChange={(next) => set('contact', { ...fields.contact, address: next })}
                required={false}
              />
            </div>

            <Input
              label={t('staff.fields.phone')}
              value={fields.contact.phone ?? ''}
              onChange={(event) => set('contact', { ...fields.contact, phone: event.target.value })}
            />

            <Input
              type="email"
              label={t('staff.fields.email')}
              value={fields.contact.email ?? ''}
              onChange={(event) => set('contact', { ...fields.contact, email: event.target.value })}
              error={errors['contact_block.email']}
            />
          </fieldset>

          <Preview fields={fields} />
        </div>

        <div className="space-y-4">
          <PublishingSidebar
            resource="pages"
            status={fields.status}
            onStatusChange={(status) => set('status', status)}
            publishedAt={fields.published_at}
            onPublishedAtChange={(next) => set('published_at', next)}
            updatedAt={record.updated_at}
            updatedBy={record.updated_by}
            pendingReview={record.pending_review}
            saving={save.isPending}
          />

          <TranslationProgress
            fields={{
              title: { label: t('modals.fields.title'), value: fields.title },
              body: { label: t('content.fields.body'), value: fields.body },
            }}
          />
        </div>
      </div>
    </form>
  );
}

/**
 * §5.3 — "a preview that renders the modal exactly as the public site does".
 *
 * The same order and the same emphases as `ContentModal`: display title, then
 * the lead at `body-lg` semibold, then the body in the prose style, then the
 * contact block with its icons, then the button.
 *
 * It renders the *edited* values rather than the saved ones, because the point
 * is catching a formatting mistake before it is saved rather than after.
 */
function Preview({ fields }: { fields: Fields }) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const body = valueFor(fields.body, locale);

  return (
    <section aria-labelledby="modal-preview" className="rounded-panel border border-subtle p-5">
      <h2 id="modal-preview" className="mb-3 text-caption font-extrabold tracking-wide text-muted">
        {t('modals.preview')}
      </h2>

      <div className="rounded-panel bg-surface p-5 shadow-sm">
        <p className="text-h3 font-extrabold text-primary">
          {valueFor(fields.title, locale) || '—'}
        </p>

        {valueFor(fields.lead, locale) ? (
          <p className="mt-3 text-body-lg font-semibold text-primary">
            {valueFor(fields.lead, locale)}
          </p>
        ) : null}

        {/*
          The same `dangerouslySetInnerHTML` the public modal uses, on the same
          HTML — the server has already purified whatever was saved, and what
          is on screen here has not been saved yet. That is the point of a
          preview, and it is the editor's own markup either way.
        */}
        {body ? (
          <div
            className="prose-sjuni prose mt-4 max-w-none text-body text-secondary"
            dangerouslySetInnerHTML={{ __html: body }}
          />
        ) : null}

        {hasContact(fields.contact) ? (
          <dl className="mt-5 space-y-1 border-t border-subtle pt-4 text-body-sm text-navy-700">
            {valueFor(fields.contact.address ?? {}, locale) ? (
              <div className="flex gap-2">
                <dt className="sr-only">{t('modals.fields.address')}</dt>
                <dd>{valueFor(fields.contact.address ?? {}, locale)}</dd>
              </div>
            ) : null}

            {fields.contact.phone ? (
              <div className="flex gap-2">
                <dt className="sr-only">{t('staff.fields.phone')}</dt>
                <dd>{fields.contact.phone}</dd>
              </div>
            ) : null}

            {fields.contact.email ? (
              <div className="flex gap-2">
                <dt className="sr-only">{t('staff.fields.email')}</dt>
                <dd>{fields.contact.email}</dd>
              </div>
            ) : null}
          </dl>
        ) : null}

        {valueFor(fields.cta_label, locale) ? (
          <p className="mt-5">
            <span className="inline-flex rounded-full bg-navy-600 px-5 py-2.5 text-body-sm font-semibold text-white">
              {valueFor(fields.cta_label, locale)}
            </span>
          </p>
        ) : null}
      </div>
    </section>
  );
}

function hasContact(contact: ModalContactBlock): boolean {
  return Object.values(contact).some((value) =>
    typeof value === 'string'
      ? value.trim() !== ''
      : typeof value === 'object' && value !== null && Object.values(value).some(Boolean),
  );
}
